import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getProducts,
  GetCategoryLevel2Products,
  GetFilteredProductsByShapes,
  ProductPdpBySearchResponse,
  GetProductsShapes,
  GetProductsFromSearch,
  GetOtcMedicineProducts,
  GetOtcProductsShapes,
  GetShoppingPDPBySearch,
} from '@api/product/productApis';

export const useGetProducts: (
  options?: UseQueryOptions<unknown, Error, any[]>,
) => UseQueryResult<any[], Error> = (options) =>
  useQuery({
    queryKey: ['getProducts'],
    queryFn: () => getProducts(),
    ...options,
  });

export const useGetCategtyL2Products = (parentCode: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['GetCategoryLevel2Products', parentCode],
    queryFn: () => GetCategoryLevel2Products(parentCode),
  });
  return { data: data as any, isLoading };
};

export const useGetProductsShapes = (categoryCodeLevel2: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['GetProductsShapes', categoryCodeLevel2],
    queryFn: () => GetProductsShapes(categoryCodeLevel2),
    enabled: !!categoryCodeLevel2,
  });
  return { data: data as any, isLoading, refetch: refetch };
};

export const useGetFilteredProductsByShapes = (
  categoryCode: string,
  shapeCode: number,
) => {
  const { data } = useQuery({
    queryKey: ['GetFilteredProductsByShapes', categoryCode, shapeCode],
    queryFn: () => GetFilteredProductsByShapes(categoryCode, shapeCode),
    enabled: !!shapeCode,
  });

  return { data: data as any };
};

export const useGetProductsFromSearch = ({
  brandName,
  categoryCodeLevel3,
  irc,
  enabled = true,
}: {
  brandName?: string;
  categoryCodeLevel3?: string;
  irc: string;
  enabled?: boolean;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['GetProductsFromSearch', irc],
    queryFn: () => GetProductsFromSearch({ brandName, categoryCodeLevel3, irc }),
    enabled: !!irc && enabled,
    staleTime: 0,
  });

  return { data: data as any, isLoading };
};

export const useGetShoppingPDPBySearch = ({
  brandName,
  categoryCodeLevel3,
  irc,
  enabled = true,
}: {
  brandName?: string;
  categoryCodeLevel3?: string;
  irc: string;
  enabled?: boolean;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['GetShoppingPDPBySearch', irc],
    queryFn: (): Promise<ProductPdpBySearchResponse> =>
      GetShoppingPDPBySearch({ brandName, categoryCodeLevel3, irc }),
    enabled: !!irc && enabled,
    staleTime: 0,
  });

  return { data, isLoading };
};

export const useGetOtcMedicineProducts = (body?: any) => {
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    enabled: !!body?.categoryCodeLevel1,
    queryKey: ['GetOtcMedicineProducts', body],
    queryFn: ({ pageParam = 1 }) => {
      return GetOtcMedicineProducts({
        ...body,
        pageNumber: body?.pageNumber ?? pageParam,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (data: any) => {
      return Math.floor(data?.totalCount / 10) + 1 === data?.pageNumber
        ? undefined
        : data?.pageNumber + 1;
    },
  });

  return {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
};

export const useGetPaginateOtcMedicineProducts = (
  body?: Record<string, unknown>,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey'>,
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>({
    queryKey: ['GetOtcMedicineProducts', body],
    queryFn: () => GetOtcMedicineProducts(body),
    enabled: !!body?.categoryCodeLevel1,
    ...options,
  });
};

export const useGetOtcProductsShapes = (categoryCodeLevel1: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['GetOtcProductsShapes', categoryCodeLevel1],
    queryFn: () => GetOtcProductsShapes(categoryCodeLevel1),
    enabled: !!categoryCodeLevel1,
  });
  return { data: data as any, isLoading, refetch: refetch };
};
